import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";

// Section eyebrow label with a leading accent dash — highlighted venture banner
const VENTURE_LABEL = "ITS A GARRY INDUSTRIES VENTURE FOR RAILWAYS PROJECTS!";
export function SectionLabel({ light = false }) {
  return (
    <div className="flex items-center gap-4">
      <span className="h-[3px] w-10 bg-[#c3e63c] shrink-0" />
      <span className="inline-block bg-[#c3e63c] px-4 py-2 font-display font-bold uppercase tracking-[0.12em] text-[#0a0a0a] text-sm md:text-xl leading-snug shadow-[6px_6px_0_rgba(195,230,60,0.25)]">
        {VENTURE_LABEL}
      </span>
    </div>
  );
}

// Reveal-on-scroll wrapper
export function Reveal({ children, className = "", delay = 0, as: Tag = "div" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <Tag ref={ref} style={{ transitionDelay: `${delay}ms` }} className={`reveal ${visible ? "is-visible" : ""} ${className}`}>
      {children}
    </Tag>
  );
}

// Primary solid accent button
export function BtnAccent({ to, href, children, className = "" }) {
  const cls = `btn-accent inline-flex items-center gap-2.5 bg-[#c3e63c] px-6 py-3.5 eyebrow text-[#0a0a0a] hover:bg-[#d4f24d] ${className}`;
  const inner = <>{children} <ArrowRight size={15} /></>;
  if (href) return <a href={href} className={cls}>{inner}</a>;
  return <Link to={to} className={cls}>{inner}</Link>;
}

// Outline / ghost button
export function BtnGhost({ to, href, children, dark = false, className = "" }) {
  const base = dark
    ? "border-[#0a0a0a]/30 text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white"
    : "border-white/25 text-white hover:bg-white hover:text-[#0a0a0a]";
  const cls = `btn-accent inline-flex items-center gap-2.5 border px-6 py-3.5 eyebrow ${base} ${className}`;
  if (href) return <a href={href} className={cls}>{children}</a>;
  return <Link to={to} className={cls}>{children}</Link>;
}

// Text link with arrow
export function ArrowLink({ to, children, light = false }) {
  return (
    <Link to={to} className={`group inline-flex items-center gap-2 eyebrow ${light ? "text-[#0a0a0a]" : "text-white"} hover:text-[#c3e63c] transition-colors`}>
      {children}
      <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  );
}

// Big page hero used by inner pages
export function PageHero({ index, eyebrow, title, subtitle, image }) {
  return (
    <section className="relative min-h-[70vh] flex items-end overflow-hidden bg-[#0a0a0a]">
      <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]/30" />
      <div className="dot-grid absolute inset-0 opacity-30" />
      <div className="relative mx-auto w-full max-w-[1400px] px-5 md:px-10 pb-16 md:pb-24 pt-32">
        {index && <div className="eyebrow text-neutral-500 mb-6">/ {index}</div>}
        <SectionLabel>{eyebrow}</SectionLabel>
        <h1 className="font-display mt-6 max-w-4xl text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-white">
          {title}
        </h1>
        {subtitle && <p className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-neutral-300">{subtitle}</p>}
      </div>
    </section>
  );
}

// Stat block
export function Stat({ value, label, light = false }) {
  return (
    <div>
      <div className={`font-display text-3xl md:text-4xl font-bold ${light ? "text-[#0a0a0a]" : "text-white"}`}>{value}</div>
      <div className="eyebrow mt-2 text-neutral-500">{label}</div>
    </div>
  );
}

export { ArrowUpRight };
